import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { textVariants } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

const frameVariants = cva(
  [
    "relative flex flex-col rounded-(--frame-radius) bg-surface-sunken px-(--frame-px) py-(--frame-py) gap-(--frame-gap)",
    "[--frame-radius:var(--radius-xl)]",
    "[--frame-gap:--spacing(0.75)] [--frame-px:--spacing(0.75)] [--frame-py:--spacing(0.75)] [--frame-panel-header-gap:0rem] [--frame-panel-footer-gap:--spacing(1)]",
    "[--frame-panel-px-adjust:0px] [--frame-panel-py-adjust:0px] [--frame-panel-header-px-adjust:0px] [--frame-panel-header-py-adjust:0px] [--frame-panel-footer-px-adjust:0px] [--frame-panel-footer-py-adjust:0px]",
    "[--frame-panel-px:calc(var(--frame-panel-px-base)+var(--frame-panel-px-adjust))] [--frame-panel-py:calc(var(--frame-panel-py-base)+var(--frame-panel-py-adjust))] [--frame-panel-header-px:calc(var(--frame-panel-header-px-base)+var(--frame-panel-header-px-adjust))] [--frame-panel-header-py:calc(var(--frame-panel-header-py-base)+var(--frame-panel-header-py-adjust))] [--frame-panel-footer-px:calc(var(--frame-panel-footer-px-base)+var(--frame-panel-footer-px-adjust))] [--frame-panel-footer-py:calc(var(--frame-panel-footer-py-base)+var(--frame-panel-footer-py-adjust))]",
    "[--frame-panel-bg:var(--surface-raised)]",
    "[--frame-panel-radius:calc(var(--frame-radius)-var(--frame-px))]",
  ],
  {
    variants: {
      variant: {
        default: "bg-clip-padding",
        inverse:
          "border border-line bg-surface bg-clip-padding [--frame-panel-bg:var(--surface-sunken)] [--frame-panel-radius:calc(var(--frame-radius)-var(--frame-px)-1px)]",
        ghost:
          "[--frame-panel-radius:calc(var(--frame-radius)-var(--frame-px))]",
      },
      spacing: {
        xs: "[--frame-panel-px-base:--spacing(2)] [--frame-panel-py-base:--spacing(2)] [--frame-panel-header-px-base:--spacing(2)] [--frame-panel-header-py-base:--spacing(0.5)] [--frame-panel-footer-px-base:--spacing(2)] [--frame-panel-footer-py-base:--spacing(0.5)]",
        sm: "[--frame-panel-px-base:--spacing(3)] [--frame-panel-py-base:--spacing(3.5)] [--frame-panel-header-px-base:--spacing(3)] [--frame-panel-header-py-base:--spacing(1.5)] [--frame-panel-footer-px-base:--spacing(3)] [--frame-panel-footer-py-base:--spacing(1.5)]",
        default:
          "[--frame-panel-px-base:--spacing(4)] [--frame-panel-py-base:--spacing(4)] [--frame-panel-header-px-base:--spacing(4)] [--frame-panel-header-py-base:--spacing(2)] [--frame-panel-footer-px-base:--spacing(4)] [--frame-panel-footer-py-base:--spacing(2)]",
        lg: "[--frame-panel-px-base:--spacing(5)] [--frame-panel-py-base:--spacing(5)] [--frame-panel-header-px-base:--spacing(5)] [--frame-panel-header-py-base:--spacing(2.5)] [--frame-panel-footer-px-base:--spacing(5)] [--frame-panel-footer-py-base:--spacing(2.5)]",
      },
      stacked: {
        true: [
          "gap-0 *:has-[+[data-slot=frame-panel]]:rounded-b-none",
          "*:has-[+[data-slot=frame-panel]]:before:hidden",
          "*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:rounded-t-none",
          "*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:border-t-0",
        ],
        false: [
          "data-[spacing=xs]:*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-0.5",
          "data-[spacing=sm]:*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-0.5",
          "data-[spacing=default]:*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-1",
          "data-[spacing=lg]:*:[[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-2",
        ],
      },
      dense: {
        true: [
          "gap-0 border border-line p-0 [--frame-panel-radius:var(--frame-radius)]",
          "[&_[data-slot=frame-panel]]:-mx-px [&_[data-slot=frame-panel]]:shadow-none [&_[data-slot=frame-panel]]:before:hidden",
          "[&_[data-slot=frame-panel]:last-child]:-mb-px",
          "[&:not(:has([data-slot=frame-panel-header]))_[data-slot=frame-panel]:is(:first-child)]:-mt-px",
          "[&>[data-slot=frame-panel-header]+[data-slot=frame-panel]]:relative [&>[data-slot=frame-panel-header]+[data-slot=frame-panel]]:z-[1] [&>[data-slot=frame-panel-header]+[data-slot=frame-panel]]:-mt-px",
        ],
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      spacing: "default",
      stacked: false,
      dense: false,
    },
  },
);

export type FrameProps = ComponentProps<"div"> &
  VariantProps<typeof frameVariants>;

const Frame = ({
  className,
  variant,
  spacing,
  stacked,
  dense,
  ...props
}: FrameProps) => (
  <div
    className={cn(
      frameVariants({ variant, spacing, stacked, dense }),
      className,
    )}
    data-slot="frame"
    data-spacing={spacing ?? "default"}
    {...props}
  />
);

const FramePanel = ({
  className,
  fit = false,
  ...props
}: ComponentProps<"div"> & { fit?: boolean }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-(--frame-panel-radius) border border-line bg-(--frame-panel-bg) bg-clip-padding px-(--frame-panel-px) py-(--frame-panel-py) shadow-xs/5",
      !fit && "grow",
      "before:pointer-events-none before:absolute before:inset-px before:rounded-[calc(var(--frame-panel-radius)-1px)] before:shadow-[0_1px_rgb(0_0_0/4%)] dark:before:shadow-[0_-1px_rgb(255_255_255/8%)]",
      className,
    )}
    data-slot="frame-panel"
    {...props}
  />
);

const FrameHeader = ({ className, ...props }: ComponentProps<"header">) => (
  <header
    className={cn(
      "flex flex-col gap-(--frame-panel-header-gap) px-(--frame-panel-header-px) py-(--frame-panel-header-py)",
      className,
    )}
    data-slot="frame-panel-header"
    {...props}
  />
);

const FrameTitle = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    className={cn(textVariants({ variant: "title" }), className)}
    data-slot="frame-panel-title"
    {...props}
  />
);

const FrameDescription = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    className={cn("text-sm text-subtle", className)}
    data-slot="frame-panel-description"
    {...props}
  />
);

const FrameFooter = ({ className, ...props }: ComponentProps<"footer">) => (
  <footer
    className={cn(
      "flex flex-col gap-(--frame-panel-footer-gap) px-(--frame-panel-footer-px) py-(--frame-panel-footer-py)",
      className,
    )}
    data-slot="frame-panel-footer"
    {...props}
  />
);

export {
  Frame,
  FramePanel,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FrameFooter,
  frameVariants,
};
